import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Radio, RadioGroup } from '@lifeomic/chroma-react/components/Radio';
import { Box } from '@lifeomic/chroma-react/components/Box';
import moment from 'moment';
import {
  getDefaultInProgressResponse,
  isCurrentUserAuthor,
} from '../util/getInProgressResponsesForSurvey';

const messages = defineMessages({
  titleOne: {
    id: 'InProgressSurveyChoiceModal.titleOne',
    defaultMessage: 'In-progress response found',
  },
  titleMany: {
    id: 'InProgressSurveyChoiceModal.titleMany',
    defaultMessage: 'In-progress responses found',
  },
  singleBody: {
    id: 'InProgressSurveyChoiceModal.singleBody',
    defaultMessage:
      'Continue the in-progress response from {date} by {author}?',
  },
  manyBody: {
    id: 'InProgressSurveyChoiceModal.manyBody',
    defaultMessage:
      'Choose an in-progress response to continue, or start a new response.',
  },
  you: {
    id: 'InProgressSurveyChoiceModal.you',
    defaultMessage: 'you',
  },
  youSuffix: {
    id: 'InProgressSurveyChoiceModal.youSuffix',
    defaultMessage: '{name} (You)',
  },
  anotherAuthor: {
    id: 'InProgressSurveyChoiceModal.anotherAuthor',
    defaultMessage: 'another author',
  },
  draftChoiceLabel: {
    id: 'InProgressSurveyChoiceModal.draftChoiceLabel',
    defaultMessage: 'In-progress responses',
  },
  cancel: {
    id: 'InProgressSurveyChoiceModal.cancel',
    defaultMessage: 'Cancel',
  },
  startNew: {
    id: 'InProgressSurveyChoiceModal.startNew',
    defaultMessage: 'Start new response',
  },
  continue: {
    id: 'InProgressSurveyChoiceModal.continue',
    defaultMessage: 'Continue',
  },
});

const formatAuthor = (
  response: fhir.QuestionnaireResponse,
  currentUserId: string | undefined,
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): string => {
  const name = response.author?.display?.trim();
  if (isCurrentUserAuthor(response, currentUserId)) {
    return name
      ? formatMessage(messages.youSuffix, { name })
      : formatMessage(messages.you);
  }
  return name || formatMessage(messages.anotherAuthor);
};

export type InProgressSurveyChoiceModalProps = {
  isOpen: boolean;
  drafts: fhir.QuestionnaireResponse[];
  currentUserId?: string;
  onDismiss: () => void;
  onContinue: (response: fhir.QuestionnaireResponse) => void;
  onStartNew: () => void;
};

const InProgressSurveyChoiceModal: React.FC<
  InProgressSurveyChoiceModalProps
> = ({ isOpen, drafts, currentUserId, onDismiss, onContinue, onStartNew }) => {
  const { formatMessage } = useIntl();
  const defaultDraft = getDefaultInProgressResponse(drafts, currentUserId);
  const [selectedId, setSelectedId] = React.useState<string | undefined>(
    defaultDraft?.id,
  );

  React.useEffect(() => {
    setSelectedId(getDefaultInProgressResponse(drafts, currentUserId)?.id);
  }, [currentUserId, drafts, isOpen]);

  const selectedDraft =
    drafts.find(draft => draft.id === selectedId) ?? defaultDraft;

  if (!isOpen || drafts.length === 0) {
    return null;
  }

  const isSingle = drafts.length === 1;
  const singleDraft = drafts[0];

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      title={formatMessage(isSingle ? messages.titleOne : messages.titleMany)}
      actions={
        <>
          <Button variant="text" onClick={onDismiss}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button variant="text" onClick={onStartNew}>
            {formatMessage(messages.startNew)}
          </Button>
          <Button
            disabled={!selectedDraft}
            onClick={() => {
              if (selectedDraft) {
                onContinue(selectedDraft);
              }
            }}
          >
            {formatMessage(messages.continue)}
          </Button>
        </>
      }
    >
      {isSingle ? (
        <Text>
          {formatMessage(messages.singleBody, {
            date: moment(singleDraft.authored).format('LLL'),
            author: formatAuthor(singleDraft, currentUserId, formatMessage),
          })}
        </Text>
      ) : (
        <Box direction="column" gap={1}>
          <Text>{formatMessage(messages.manyBody)}</Text>
          <RadioGroup
            title=""
            value={selectedId}
            onChange={e => setSelectedId(e.target.value)}
            aria-label={formatMessage(messages.draftChoiceLabel)}
          >
            {drafts.map(draft =>
              draft.id ? (
                <Radio
                  key={draft.id}
                  value={draft.id}
                  label={`${moment(draft.authored).format(
                    'LLL',
                  )} · ${formatAuthor(draft, currentUserId, formatMessage)}`}
                />
              ) : null,
            )}
          </RadioGroup>
        </Box>
      )}
    </Modal>
  );
};

export default InProgressSurveyChoiceModal;
